import { useMutation, useQueryClient } from 'react-query'
import { postJSON } from 'common/services/api'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'

dayjs.extend(utc)
import {
  CURRENT_STOCK_QUERY_KEY,
  ADD_STOCK_BATCH_URL,
  STOCK_LOGS_QUERY_KEY,
  STOCK_DISCREPANCIES_QUERY_KEY
} from './queryConstants'
import { AddStockData, MedicationStockReason } from '../types'
import { useUpdateStockRecord } from './useUpdateStockRecord'
import useMixpanelEvent, { MixpanelEvents } from 'hooks/useMixpanelEvent'

type UseAddStockProps = {
  residentId: string
  residentMedicationId: string
  medicationName?: string
  onSuccess?: () => void
  onError?: (error: any) => void
}

type AddStockMedication = {
  residentId: string
  residentMedicationId: string
  quantity: number
  notes: string
}

type AddStockBatchItem = {
  medications: AddStockMedication[]
  actionTime?: string
  deliveryNotes: string
  witnessIds: string[]
  receivedBy?: { id: string }
  receivedFrom?: string
  media?: string[]
}

export const useAddStock = ({
  residentId,
  residentMedicationId,
  medicationName,
  onSuccess,
  onError
}: UseAddStockProps) => {
  const queryClient = useQueryClient()
  const sendEvent = useMixpanelEvent()

  const { updateStockRecord, isLoading: isUpdating } = useUpdateStockRecord({
    onSuccess,
    onError
  })

  const createMutation = useMutation(
    (data: AddStockData) => {
      const actionTime =
        data.actionTime &&
        typeof data.actionTime.isValid === 'function' &&
        data.actionTime.isValid()
          ? dayjs
              .utc(data.actionTime.format('YYYY-MM-DD'))
              .startOf('day')
              .toISOString()
          : undefined

      const medication: AddStockMedication = {
        residentId,
        residentMedicationId,
        quantity: data.receivedStock,
        notes: data.description
      }

      const hasMedia = data.media && data.media.length > 0

      const batchItem: AddStockBatchItem = {
        medications: [medication],
        ...(actionTime !== undefined && { actionTime }),
        deliveryNotes: data.description,
        witnessIds: [],
        receivedBy: data.receivedBy ? { id: data.receivedBy } : undefined,
        receivedFrom: data.receivedFrom || undefined,
        media: hasMedia ? [] : undefined
      }

      if (hasMedia && data.media) {
        const formData = new FormData()
        formData.append('batch', JSON.stringify(batchItem))

        data.media.forEach((file, index) => {
          formData.append(`batch.media[${index}]`, file)
        })

        return postJSON({
          url: ADD_STOCK_BATCH_URL,
          contentType: 'multipart/form-data',
          body: formData
        })
      }

      return postJSON({
        url: ADD_STOCK_BATCH_URL,
        json: batchItem
      })
    },
    {
      onSuccess: () => {
        if (medicationName) {
          sendEvent(MixpanelEvents.EMAR_V2_STOCK_RECORD_CREATED, {
            medicationName,
            typeOfRecord: 'Add Stock',
            time: dayjs().toISOString(),
            discrepanciesFlagged: false,
            discrepanciesClosed: false
          })
        }

        queryClient.invalidateQueries([STOCK_LOGS_QUERY_KEY])
        queryClient.invalidateQueries([CURRENT_STOCK_QUERY_KEY])
        queryClient.invalidateQueries([STOCK_DISCREPANCIES_QUERY_KEY])
        onSuccess?.()
      },
      onError: (error) => {
        console.error('Error saving add stock record:', error)
        onError?.(error)
      }
    }
  )

  const updateAddStock = async ({
    recordId,
    data
  }: {
    recordId: string
    data: AddStockData
  }) => {
    let actionTime: string | undefined

    if (
      data.actionTime &&
      typeof data.actionTime.isValid === 'function' &&
      data.actionTime.isValid()
    ) {
      actionTime = dayjs
        .utc(data.actionTime.format('YYYY-MM-DD'))
        .startOf('day')
        .toISOString()
    }

    return updateStockRecord({
      recordId,
      type: MedicationStockReason.RESTOCK,
      data: {
        notes: data.description,
        witnessIds: [],
        receivedBy: data.receivedBy || undefined,
        receivedFrom: data.receivedFrom || undefined,
        actionTime,
        media: data.media,
        mediaAttachmentKeysToRemove: data.mediaAttachmentKeysToRemove
      }
    })
  }

  return {
    createAddStock: createMutation.mutateAsync,
    updateAddStock,
    isLoading: createMutation.isLoading || isUpdating
  }
}

export default useAddStock
